import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { patchJSON, postJSON } from '@/lib/api'
import { PipelineActionDialog } from '@/pages/RenewalPipelineActions'
import type { PipelineAction, PipelineRow } from '@/pages/RenewalPipelinePage.types'

export function RenewalPipelineActionDialogs({
  action,
  currentAssociationDate,
  onClose,
  onResolved,
}: {
  action: PipelineAction | null
  currentAssociationDate?: string
  onClose: () => void
  onResolved?: (details?: { holdEndsOn?: string }) => void | Promise<void>
}) {
  if (action?.kind === 'renew')
    return <RenewFreeMembershipDialog row={action.row} onClose={onClose} onResolved={onResolved} />
  if (action?.kind === 'hold')
    return <EscalationHoldDialog row={action.row} onClose={onClose} onResolved={onResolved} />

  return (
    <PipelineActionDialog
      action={action}
      currentAssociationDate={currentAssociationDate}
      onClose={onClose}
      onResolved={onResolved}
    />
  )
}

function EscalationHoldDialog({
  row,
  onClose,
  onResolved,
}: {
  row: PipelineRow
  onClose: () => void
  onResolved?: (details?: { holdEndsOn?: string }) => void | Promise<void>
}) {
  const queryClient = useQueryClient()
  const [endsOn, setEndsOn] = React.useState('')
  const [note, setNote] = React.useState('')
  const placement = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/persons/${row.person_id}/membership/escalation_hold`, {
        escalation_hold: { ends_on: endsOn, note },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['renewal-pipeline'] })
      await onResolved?.({ holdEndsOn: endsOn })
      onClose()
    },
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Hold {row.name}&apos;s escalation</DialogTitle>
          <DialogDescription>
            Reminder touches and the lapse clock pause until this date. Autopay continues.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="hold-ends-on">Hold through</Label>
            <Input
              id="hold-ends-on"
              type="date"
              value={endsOn}
              onChange={(event) => setEndsOn(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="hold-note">Note</Label>
            <Input
              id="hold-note"
              value={note}
              placeholder="Why is escalation held?"
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          {placement.isError && <Alert>The Escalation Hold could not be placed.</Alert>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={placement.isPending || endsOn.length === 0 || note.trim().length === 0}
            onClick={() => placement.mutate()}
          >
            {placement.isPending ? 'Placing hold…' : 'Place hold'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RenewFreeMembershipDialog({
  row,
  onClose,
  onResolved,
}: {
  row: PipelineRow
  onClose: () => void
  onResolved?: () => void | Promise<void>
}) {
  const queryClient = useQueryClient()
  const renewal = useMutation({
    mutationFn: () =>
      patchJSON(`/api/v1/persons/${row.person_id}/membership/renew`, {
        membership: { expected_renewal_due_on: row.renewal_due_on },
      }),
    onSuccess: async () => {
      await onResolved?.()
      onClose()
      await queryClient.invalidateQueries({ queryKey: ['renewal-pipeline'] })
    },
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Renew {row.level}</DialogTitle>
          <DialogDescription>
            Keep this free Membership Active and advance its renewal-due date by one term.
          </DialogDescription>
        </DialogHeader>
        {renewal.isError && (
          <Alert>The Membership could not be renewed. Refresh and try again.</Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={renewal.isPending} onClick={() => renewal.mutate()}>
            {renewal.isPending ? 'Renewing…' : 'Renew membership'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
